// This component implements a very generic floating window.
// It is very unlikely that you need to use this component directly,
// Please consider using `Dialog`, `Dropdown`, or `Tooltip` instead.

import React from 'react'
import ReactDOM from 'react-dom'
import cn from 'classnames'
import { observer } from 'mobx-react'

import Mask from 'components/mask/Mask'

import getModalPosition from 'utils/getModalPosition'

import styles from './modal.scss'

interface IProps {
  className?: string
  maskClassName?: string
  domElementId: string /* ID of the DOM element this modal will React.createPortal at */
  target?: React.RefObject<any> /* A ref object bound to a component this modal must open next to */
  targetOffsetX: number /* Horizontal distance from the target this modal should open */
  targetOffsetY: number /* Vertical distance from the target this modal should open */
  darken: boolean /* Whether or not to darken everything around the modal */
  children: React.ReactChild /* Body of the modal */
  onClickOutside?: (
    e: React.MouseEvent<HTMLDivElement>
  ) => void /* Function to call when the mask is clicked */
}

interface IState {
  top?: number
  bottom?: number
  left?: number
  right?: number
  minWidth?: number
  maxWidth?: number
  minHeight?: number
  maxHeight?: number
}

class Modal extends React.PureComponent<IProps, IState> {
  public static defaultProps = {
    domElementId: 'modal-root',
    targetOffsetX: 0,
    targetOffsetY: 5,
    darken: false,
  }

  state = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    minWidth: undefined,
    maxWidth: undefined,
    minHeight: undefined,
    maxHeight: undefined,
  }

  componentDidMount() {
    this.setPosition(this.props)
  }

  componentWillReceiveProps(nextProps: IProps) {
    this.setPosition(nextProps)
  }

  setPosition({ target, targetOffsetX, targetOffsetY }: IProps) {
    if (!target) {
      return
    }

    this.setState(
      getModalPosition(target, {
        x: targetOffsetX,
        y: targetOffsetY,
      })
    )
  }

  render() {
    const {
      className,
      maskClassName,
      domElementId,
      target,
      targetOffsetX,
      targetOffsetY,
      darken,
      children,
      onClickOutside,
      ...other
    } = this.props

    return (
      <>
        {ReactDOM.createPortal(
          <>
            <Mask
              className={cn(
                {
                  [styles.transparent]: !darken,
                },
                maskClassName
              )}
              onClick={onClickOutside}
            />
            <div
              data-cy="modal"
              className={cn(styles.container, className)}
              style={target && this.state}
              {...other}
            >
              {children}
            </div>
          </>,
          document.getElementById(domElementId)!
        )}
      </>
    )
  }
}

export default observer(Modal)
